import { format } from 'date-fns'
import { IdentificationRateDataPoint, TotalIdentificationRateWidget } from '../response-types'
import { SSOAnalyticsTimeScale } from '../common/types'
import { isArray } from 'lodash'
import { ComparisonDataType } from 'src/components/analytics/types/comparison-line-chart'

const EMPTY_DATA = {
  currentWeekData: [],
  previousWeekData: [],
  primaryDataset: [],
  secondaryDataset: [],
  isCurrentPrimary: false,
  currentDateRange: {
    from: new Date(),
    to: new Date()
  },
  previousDateRange: {
    from: new Date(),
    to: new Date()
  },
  totalIdentifications: 0,
  totalVisitors: 0,
  totalIdentificationRate: 0
}

export function parseTotalIdentificationRateWidgetData(
  widgetData: TotalIdentificationRateWidget | [],
  selectedTimeScale: SSOAnalyticsTimeScale
): {
  currentWeekData: ComparisonDataType[]
  previousWeekData: ComparisonDataType[]
  primaryDataset: ComparisonDataType[]
  secondaryDataset: ComparisonDataType[]
  isCurrentPrimary: boolean
  currentDateRange: { from: Date; to: Date }
  previousDateRange: { from: Date; to: Date } | null
  totalIdentifications: number
  totalVisitors: number
  totalIdentificationRate: number
} {
  if (isArray(widgetData) || !widgetData) {
    return EMPTY_DATA
  }

  const rawData = widgetData.widgetData.data.data
  const rawComparedData = widgetData.widgetData.comparedData.data
  if (rawData && rawData.every(item => item.total_user === 0)) {
    if (rawComparedData && rawComparedData.every(item => item.total_user === 0)) {
      return EMPTY_DATA
    }
    return EMPTY_DATA
  }

  if (!rawData || rawData.length === 0) {
    return EMPTY_DATA
  }

  const sortDataByDate = (prevData: IdentificationRateDataPoint, currData: IdentificationRateDataPoint) => {
    const prevDate = new Date(prevData.date)
    const currDate = new Date(currData.date)
    return prevDate.getTime() - currDate.getTime()
  }

  // Sort both datasets
  const currentData = rawData.sort(sortDataByDate)
  let previousData: IdentificationRateDataPoint[] = []
  if (rawComparedData) {
    previousData = rawComparedData.sort(sortDataByDate)
  }

  // Calculate date ranges
  const currentRange =
    new Date(currentData[currentData.length - 1].date).getTime() - new Date(currentData[0].date).getTime()
  let previousRange = 0
  if (previousData.length > 0) {
    previousRange =
      new Date(previousData[previousData.length - 1].date).getTime() - new Date(previousData[0].date).getTime()
  }

  // Determine which dataset has the larger range
  const isCurrentPrimary = currentRange >= previousRange
  const primaryDataset = isCurrentPrimary ? currentData : previousData

  const calculateTotalIdentificationRate = (data: IdentificationRateDataPoint[]): number => {
    const totalUsers = data.reduce((acc, curr) => acc + curr.total_user, 0)
    if (totalUsers === 0) return 0
    return (data.reduce((acc, curr) => acc + curr.identified_by_shopflo, 0) / totalUsers) * 100
  }

  const calculateRate = (item: IdentificationRateDataPoint): number => {
    if (item.total_user === 0 || item.identified_by_shopflo === 0) return 0
    return parseFloat(((item.identified_by_shopflo / item.total_user) * 100).toFixed(2))
  }

  const getTimeIncrement = () => {
    switch (selectedTimeScale) {
      case 'hourly':
        return 60 * 60 * 1000 // 1 hour in milliseconds
      case 'daily':
      default:
        return 24 * 60 * 60 * 1000 // 1 day in milliseconds
    }
  }

  // Map the data with aligned dates for display
  const mappedCurrentData = currentData.map((item, index) => ({
    date: new Date(item.date),
    displayDate: new Date(new Date(primaryDataset[0].date).getTime() + index * getTimeIncrement()),
    rate: calculateRate(item),
    actualDate: new Date(item.date)
  }))

  const mappedPreviousData = previousData.map((item, index) => ({
    date: new Date(item.date),
    displayDate: new Date(new Date(primaryDataset[0].date).getTime() + index * getTimeIncrement()),
    rate: calculateRate(item),
    actualDate: new Date(item.date)
  }))

  return {
    currentWeekData: mappedCurrentData,
    previousWeekData: mappedPreviousData,
    primaryDataset: isCurrentPrimary ? mappedCurrentData : mappedPreviousData,
    secondaryDataset: isCurrentPrimary ? mappedPreviousData : mappedCurrentData,
    isCurrentPrimary,
    currentDateRange: {
      from: new Date(currentData[0].date),
      to: new Date(currentData[currentData.length - 1].date)
    },
    previousDateRange:
      previousData.length > 0
        ? {
            from: new Date(previousData[0].date),
            to: new Date(previousData[previousData.length - 1].date)
          }
        : null,
    totalIdentifications: currentData.reduce((acc, curr) => acc + curr.identified_by_shopflo, 0),
    totalVisitors: currentData.reduce((acc, curr) => acc + curr.total_user, 0),
    totalIdentificationRate: calculateTotalIdentificationRate(currentData)
  }
}

export const formatDateRange = (from: Date, to: Date, selectedTimeScale: SSOAnalyticsTimeScale) => {
  const isSameDay =
    from.getDate() === to.getDate() && from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()

  if (selectedTimeScale === 'hourly') {
    if (isSameDay) {
      return `${format(from, 'MMM d, yyyy')}`
    }
    return `${format(from, 'MMM d HH:mm')}-${format(to, 'MMM d HH:mm, yyyy')}`
  }

  if (from.getMonth() === to.getMonth()) {
    return `${format(from, 'MMM d')}-${format(to, 'd, yyyy')}`
  }
  return `${format(from, 'MMM d, yyyy')}-${format(to, 'MMM d, yyyy')}`
}
